import type { ComponentProps, ReactElement } from 'react'
import { IPage } from '@types'
import Layout from 'components/layouts/Layout'
import ProductView from 'components/product/ProductView'
import { buildProductRuntime } from 'lib/productFactsAdapter'
import { APP_ORIGIN, BASE_URL } from 'config/constants'
import fetcher from 'lib/fetcher'
import { getAllProducts } from 'lib/getAllProducts'
import { getProductDetail } from 'lib/getProductDetail'
import { setCacheControl } from 'lib/misc'
import { getCanonicalCategory } from 'lib/productCanonical'
import { isProductSlug } from 'lib/productSlug'
import { isAbsent, isSame } from 'lib/utils'
import {
  GetServerSideProps,
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from 'next'

import { ICategory } from 'store/api'

type ProductServerProps = ComponentProps<typeof ProductView> & {
  title: string
  description: string
  keywords: string
}

export let getServerSideProps: GetServerSideProps<ProductServerProps> = async ({
  params,
  res,
}: GetServerSidePropsContext) => {
  // Только /product/<категория>/<товар>: лишние сегменты — 404 до загрузки данных
  if (!isProductSlug(params?.['slug'])) return { notFound: true as const }

  let slug = params?.['slug'] as string[] | undefined
  if (!Array.isArray(slug) || slug.length !== 2 || slug.some((part) => !part))
    return { notFound: true as const }

  let { data: categoryData } = await fetcher<{ categories: ICategory[]}>({
    url: `${BASE_URL}/v1/categories/getall`,
    headers: { lng: 'ru' },
  })

  let category = categoryData?.categories.find((x) =>
    isSame(x.slug, slug![0])
  )
  if (isAbsent(category)) return { notFound: true as const }

  let { products: allProducts } = await getAllProducts('ru')
  let products = allProducts[category.id]
  if (isAbsent(products)) return { notFound: true as const }

  let product = products.find((p) => p.slug === slug![1])
  if (isAbsent(product)) return { notFound: true as const }

  // Товар существует в каждой своей категории (/product/<cat>/<slug>), и
  // до 18.09 каждая версия объявляла canonical на себя — дубли не
  // склеивались. Канонический адрес один: первая постоянная категория
  // по порядку каталога, «Новинки» — только если других нет.
  let canonicalCategory =
    getCanonicalCategory(
      categoryData?.categories ?? [],
      allProducts ?? {},
      product
    ) ?? category
  let canonical = `${APP_ORIGIN}/product/${canonicalCategory.slug}/${product.slug}`

  const productDetail = await getProductDetail(product.id)

  if (isAbsent(productDetail))
    return { notFound: true as const }

  const productRuntime = buildProductRuntime({
    product, category: canonicalCategory, canonical, productDetail,
    price: product.price, weight: product.weight, nutrition: productDetail.product,
  })

  setCacheControl(res)

  return {
    props: {
      title: productRuntime.title,
      description: productRuntime.description,
      keywords: product.meta.product_keywords ?? '',
      canonical,
      categorySSR: category,
      productSSR: product,
      productDetailSSR: productDetail,
    },
  }
}

export type ProductPageProps = Required<
  InferGetServerSidePropsType<typeof getServerSideProps>
>

let ProductPage: IPage<ProductPageProps> = (props) => (
  <ProductView key={props.productSSR.id} {...props} />
)

ProductPage.getLayout = (page: ReactElement<ProductPageProps>) => (
  <Layout
    title={page.props?.title}
    description={page.props?.description}
    keywords={page.props?.keywords}
    index
    withCartPanel
    canonical={page.props?.canonical}
  >
    {page}
  </Layout>
)

export default ProductPage
